<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue'
import { useId } from 'vue'
import Panel from 'primevue/panel'

const id = useId()

const props = defineProps<{
    label: string
    collapsable?: boolean
    inputId?: string
    requiredIndicator?: boolean
    optionalIndicator?: boolean
    fluid?: boolean
    noRequiredCheck?: boolean
    inline?: boolean
    reversed?: boolean
    noPadding?: boolean
    tooltip?: string
    helpText?: string
    divClass?: string
    size?: 'small' | 'medium' | 'large'
}>()

const element = useTemplateRef('wrapperRef')

const idName = ref<string | undefined>(props.inputId)

const isRequired = ref(false)

// Enforce accessibility by automatically adding an id for the label's for tag
watch(element, () => {
    const firstChild = element.value?.querySelector(
        'input, select, textarea, .p-radiobutton-group'
    ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

    if (!props.noRequiredCheck)
        isRequired.value = !!element.value?.querySelector('[required]:not([required=false])')

    if (!firstChild) return

    if (!firstChild.id) firstChild.id = `input-id-${id}`

    idName.value = firstChild.id
})
</script>
<template>
    <Panel
        class="p-0 border-none"
        :class="{
            'w-full': fluid,
            'flex gap-2 items-center': inline,
            'flex-row-reverse justify-end': reversed,
            'mb-3': !noPadding,
        }"
        :pt="{ content: 'p-0', header: 'p-0' }"
        :toggleable="collapsable"
        unstyled
    >
        <template #header>
            <label
                :for="idName ?? ''"
                class="flex items-center justify-between text-neutral-600"
                :class="{
                    'mb-1': !inline,
                    'text-sm': size === 'small',
                    'text-xl': size === 'large',
                }"
            >
                <span>
                    {{ label }}
                    <span v-if="requiredIndicator || isRequired" aria-hidden="true">*</span>
                </span>
                <span v-if="optionalIndicator" class="text-neutral-600 text-sm mx-4">
                    {{ $t('ui.common.optional') }}
                </span>
            </label>
        </template>

        <div ref="wrapperRef" :class="divClass">
            <slot />
            <small v-if="helpText" class="block mt-1 text-sm text-neutral-600">
                {{ helpText }}
            </small>
        </div>
    </Panel>
</template>
